import { Middleware } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import {
  setSequenceNumber,
  incrementSequence,
  bufferMessage,
  removeBufferedMessages,
  forceProcessBuffered,
  updateLastMessageTimestamp,
  selectSequenceNumber,
  selectBufferedMessages
} from '../slices/websocketSlice';
import {
  selectAuthUserId
} from '../slices/authSlice';
import {
  moveReceived,
  timeUpdated
} from '../slices/movesSlice';
import {
  leaderboardReceived
} from '../slices/leaderboardSlice';
import {
  lobbyUpdated
} from '../slices/tournamentsSlice';
import {
  tournamentCancelled,
  tournamentOver,
  tournamentOverDraw,
  nextRoundStarting,
  byeRoundReceived,
  gameStarting,
  gameEnded
} from '../slices/gameSessionSlice';
import { navigationRequested } from '../slices/navigationSlice';
import {
  setActiveView,
  showModal,
  addNotification,
  setAbandonmentWarning,
  clearAbandonmentWarning
} from '../slices/uiSlice';
import {
  poolStateReceived,
  poolCommandRejected
} from '../slices/poolSlice';
import {
  poolTournamentStateReceived,
  poolTournamentCommandRejected
} from '../slices/poolTournamentSlice';
import {
  poolTournamentLifecycleUpdated,
  poolSessionCommandRejected,
} from '../slices/poolSessionSlice';

const BUFFER_TIMEOUT_MS = 5000;

// Get ordering key from message (matches backend logic)
const getOrderingKey = (message: any): string => {
  if (message.game_id) {
    return `game:${message.game_id}`;
  }
  if (message.tournament_id) {
    return `tournament:${message.tournament_id}`;
  }
  return ''; // No ordering needed
};

// Process buffered messages that can now be executed
const processBufferedMessagesForKey = (store: any, orderingKey: string) => {
  const state = store.getState() as RootState;
  const buffered = selectBufferedMessages(state, orderingKey);

  if (buffered.length === 0) return;

  // Sort by sequence
  const sorted = [...buffered].sort((a, b) => a.sequence - b.sequence);

  const processed: string[] = [];
  const expectedSeq = selectSequenceNumber(state, orderingKey);

  for (const msg of sorted) {
    const currentExpected = selectSequenceNumber(store.getState(), orderingKey);

    if (msg.sequence === currentExpected) {
      processMessageData(store, msg.data);
      store.dispatch(incrementSequence(orderingKey));
      processed.push(`${orderingKey}-${msg.sequence}`);
    } else if (msg.sequence < currentExpected) {
      // Already processed, remove from buffer
      processed.push(`${orderingKey}-${msg.sequence}`);
    } else {
      // Can't process yet, still waiting
      break;
    }
  }

  if (processed.length > 0) {
    store.dispatch(removeBufferedMessages(processed));
  }
};

/**
 * GOLDEN STANDARD: Pure Event Orchestrator
 *
 * This middleware is a PURE EVENT-TO-ACTION MAPPER.
 * - Maps WebSocket events → Redux actions
 * - NO business logic (state machine handles that)
 * - NO state checking (reducers validate transitions)
 * - NO manual cleanup (reducers auto-cleanup on transitions)
 */
const processMessageData = (store: any, data: any) => {
  const { type, data: payload, sequence } = data;

  switch (type) {
    // ==========================================
    // TOURNAMENT EVENTS → gameSession actions
    // ==========================================
    case 'tournament_cancelled':
      store.dispatch(tournamentCancelled(payload));
      store.dispatch(navigationRequested({ path: '/chess' }));
      store.dispatch(addNotification({
        message: 'Tournament has been cancelled',
        type: 'warning'
      }));
      break;

    case 'tournament_over':
      store.dispatch(tournamentOver(payload));
      store.dispatch(showModal('tournamentOver'));
      break;

    case 'tournament_over_draw':
      store.dispatch(tournamentOverDraw(payload));
      store.dispatch(showModal('tournamentOver'));
      break;

    case 'next_round':
      store.dispatch(clearAbandonmentWarning());
      store.dispatch(nextRoundStarting(payload));
      store.dispatch(addNotification({
        message: `Round ${payload.round} starting soon`,
        type: 'info'
      }));
      break;

    case 'bye':
      store.dispatch(byeRoundReceived(payload));
      store.dispatch(showModal('bye'));
      // Navigate to tournament page so user sees the bye state (consistent with game_start)
      store.dispatch(navigationRequested({
        path: `/chess/${payload.tournamentId}`
      }));
      store.dispatch(addNotification({
        message: 'You received a bye - 0.5 points awarded!',
        type: 'info'
      }));
      break;

    // ==========================================
    // GAME EVENTS → gameSession actions
    // ==========================================
    case 'game_start':
      // Validate user is a participant using global auth state (not component-scoped state)
      {
        const authUserId = selectAuthUserId(store.getState() as RootState);
        const isParticipant = authUserId !== null &&
          (payload.whiteId === authUserId || payload.blackId === authUserId);

        if (!isParticipant) {
          // User is not in this game - ignore the event
          // (Backend should only send to participants, but this is defense-in-depth)
          break;
        }

        store.dispatch(clearAbandonmentWarning());
        store.dispatch(gameStarting(payload));
        store.dispatch(navigationRequested({
          path: `/chess/${payload.tournamentId}/${payload.gameId}`,
          state: { gameData: payload }
        }));
        store.dispatch(setActiveView('game'));
        store.dispatch(addNotification({
          message: 'Game starting!',
          type: 'info'
        }));
      }
      break;

    case 'gameover':
      store.dispatch(clearAbandonmentWarning());
      store.dispatch(gameEnded(payload));
      store.dispatch(showModal('endGame'));
      break;

    // ==========================================
    // DATA UPDATES (not state transitions)
    // ==========================================
    case 'move':
      // Any valid move invalidates current abandonment warning state.
      store.dispatch(clearAbandonmentWarning());
      store.dispatch(moveReceived({ move: payload, sequence: sequence || 0 }));
      if (payload.gameId && (payload.whiteTimeMs !== undefined || payload.blackTimeMs !== undefined)) {
        store.dispatch(timeUpdated({
          gameId: payload.gameId,
          time: {
            whiteTimeMs: payload.whiteTimeMs,
            blackTimeMs: payload.blackTimeMs,
            activePlayer: payload.activePlayer,
            clockPhase: payload.clockPhase
          }
        }));
      }
      break;

    case 'clock_tick':
      if (payload.gameId) {
        store.dispatch(timeUpdated({
          gameId: payload.gameId,
          time: {
            whiteTimeMs: payload.whiteTimeMs,
            blackTimeMs: payload.blackTimeMs,
            activePlayer: payload.activePlayer,
            clockPhase: payload.clockPhase
          }
        }));
      }
      break;

    case 'leaderboard_update':
      store.dispatch(leaderboardReceived({
        tournamentId: payload.tournamentId,
        leaderboard: payload.leaderboard,
        timestamp: Date.now()
      }));
      break;

    case 'lobby_update':
      store.dispatch(lobbyUpdated(payload));
      break;
    case 'pool_state':
      store.dispatch(poolStateReceived(payload));
      break;
    case 'pool_command_rejected':
      store.dispatch(poolCommandRejected(payload?.reason || 'Pool command rejected'));
      break;
    case 'pool_tournament_state':
      store.dispatch(poolTournamentStateReceived(payload));
      store.dispatch(poolTournamentLifecycleUpdated(payload));
      break;
    case 'pool_tournament_command_rejected':
      store.dispatch(poolTournamentCommandRejected(payload?.reason || 'Pool tournament command rejected'));
      store.dispatch(poolSessionCommandRejected(payload?.reason || 'Pool tournament command rejected'));
      break;

    case 'abandonment_warning':
      // Player hasn't made first move - show countdown warning (non-blocking)
      store.dispatch(setAbandonmentWarning({
        color: payload.color,
        secondsRemaining: payload.secondsRemaining,
        gameId: payload.gameId
      }));
      break;

    // Connection/control frames (not domain events)
    case 'connection_established':
    case 'pong':
    case 'ping':
      break;

    default:
      // Keep unknown event visibility in development only.
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[WebSocket Middleware] Unknown message type: ${type}`);
      }
  }
};

// WebSocket middleware that handles message ordering and dispatching
const websocketMiddleware: Middleware = (store) => (next) => (action: any) => {
  // Pass action through first
  const result = next(action);

  // Handle WebSocket message received action
  if (action.type === 'websocket/messageReceived') {
    const data = action.payload;
    const sequence = data.sequence || 0;
    const orderingKey = getOrderingKey(data);

    // Update last message timestamp
    store.dispatch(updateLastMessageTimestamp());

    // If no ordering key or sequence is 0, process immediately
    if (!orderingKey || sequence === 0) {
      processMessageData(store, data);
      return result;
    }

    const state = store.getState() as RootState;
    const expectedSeq = selectSequenceNumber(state, orderingKey);

    if (sequence === expectedSeq) {
      // Perfect! Process immediately
      processMessageData(store, data);
      store.dispatch(incrementSequence(orderingKey));

      // Try to process any buffered messages
      processBufferedMessagesForKey(store, orderingKey);
    } else if (sequence > expectedSeq) {
      // Future message arrived early - buffer it
      store.dispatch(bufferMessage({
        orderingKey,
        sequence,
        data,
        bufferedAt: Date.now()
      }));

      // Set timeout to force-process if we never receive expected message
      setTimeout(() => {
        const currentState = store.getState() as RootState;
        const currentExpected = selectSequenceNumber(currentState, orderingKey);

        if (currentExpected === expectedSeq) {
          store.dispatch(forceProcessBuffered(orderingKey));
          processBufferedMessagesForKey(store, orderingKey);
        }
      }, BUFFER_TIMEOUT_MS);
    }
  }

  return result;
};

export default websocketMiddleware;
